import { getAuth } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import {
  Button,
  Group,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconSend, IconHeart } from "@tabler/icons-react";
import { fetchUser, updateUserConnections } from "./ProfileUtil";
import { UserType } from "../utils/types";
import { countries } from "../utils/countries";
import * as lists from "../utils/listValues";

import ChatWindow from "../components/ChatWindow";
import UserAvatar from "../components/UserAvatar";
import { createChatHistory, fetchChatHistory } from "../components/ChatUtil";
import { deleteCurrentUser } from "../pages/ProfileUtil";

type UserProfileParams = {
  id: string;
};

export default function UserProfile() {
  const auth = getAuth();
  const intl = useIntl();
  const navigate = useNavigate();
  const { id } = useParams<UserProfileParams>();
  const [user, setUser] = useState<UserType | null>(null);
  const [loggedinUser, setLoggedinUser] = useState<UserType | null>(null);
  const [chatOpened, setChatOpened] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connections, setConnections] = useState<string[]>([]);

  //const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    photoURL: user?.photoURL ?? "",
    country: user?.country ?? "",
    city: user?.city ?? "",
    reason: user?.reason ?? "",
    profArea: user?.profArea ?? "",
    availability: user?.availability ?? "",
    description: user?.description ?? "",
    salary: user?.salary ?? 0,
  });

  const {
    name,
    email,
    photoURL,
    country,
    city,
    reason,
    profArea,
    availability,
    description,
    salary,
  } = formData;

  useEffect(() => {
    if (id) {
      fetchUser(id, setUser);
    }
  }, [id]);

  useEffect(() => {
    if (user) {
      const users = [auth.currentUser?.uid ?? "", user?.id ?? ""];
      const chatId = users.sort((a, b) => a.localeCompare(b)).join("");
      if (auth.currentUser) {
        createChatHistory(chatId, auth.currentUser.uid);
      }
      if (user.id === process.env.REACT_APP_SR_USER_ID) setChatOpened(true);
    }
  }, [user]);

  useEffect(() => {
    if (auth.currentUser?.uid) {
      fetchUser(auth.currentUser?.uid, setLoggedinUser);
    }
  }, [auth.currentUser?.uid]);

  useEffect(() => {
    if (loggedinUser && user) {
      const myConnections = loggedinUser.connections ?? [];
      setConnections(myConnections);
      const isMyConnection = myConnections.includes(user.id);
      if (isMyConnection) setIsConnected(true);
      else setIsConnected(false);
    }
  }, [loggedinUser, user]);

  useEffect(() => {
    if (user) {
      setFormData((prevState) => ({
        ...prevState,
        name: user.name,
        email: user.email,
        photoURL: user.photoURL,
        country: user.country,
        city: user.city,
        reason: user.reason,
        profArea: user.profArea,
        availability: user.availability,
        description: user.description,
        salary: user.salary,
      }));
    }
  }, [user, auth.currentUser?.uid]);

  const addUserToConnections = async (id: string) => {
    if (connections) {
      if (!connections.includes(id)) {
        const newConnections = [...connections, id];
        console.log("Updated connections after add:", newConnections);
        await updateLoggedinUserConnections(newConnections);
        setConnections(newConnections);
      }
    }
  };

  const removeUserFromConnections = async (id: string) => {
    const newConnections = connections.filter((x) => x !== id);
    console.log("Updated connections after remove:", newConnections);
    await updateLoggedinUserConnections(newConnections);
    setConnections(newConnections);
  };

  async function updateLoggedinUserConnections(newConnections: string[]) {
    try {
      if (auth.currentUser)
        await updateUserConnections(auth.currentUser, newConnections);
      toast.success("Connections updated");
    } catch (error) {
      toast.error("Could not update connections");
    }
  }

  const handleConnectedClick = () => {
    setIsConnected(!isConnected);
    if (user) {
      if (!isConnected) addUserToConnections(user.id);
      else removeUserFromConnections(user.id);
      console.log("After add remove connections", connections);
    }
  };
  const onCloseButtonClick = () => {
    navigate("/");
  };

  const onSendMessageClick = async () => {
    const users = [auth.currentUser?.uid ?? "", user?.id ?? ""];
    const chatId = users.sort((a, b) => a.localeCompare(b)).join("");
    if (auth.currentUser) {
      await createChatHistory(chatId, auth.currentUser.uid);
    }
    setChatOpened(!chatOpened);
  };

  const onDeleteAccountClick = async () => {
    if (user) {
      deleteCurrentUser(user.id).then(() => {
        navigate("/");
      });
    }
  };

  const chatWindow =
    user && chatOpened ? (
      <ChatWindow
        toggleChatForm={chatOpened}
        toUser={user}
        onClose={() => setChatOpened(false)}
      />
    ) : null;

  return (
    <>
      {chatWindow}
      <div className="profile-container">
        <h1 className="profile-heading">
          {user?.name ? user.name : "User Profile"}
        </h1>
        <div className="profile-form-container">
          <div className="profile-form-avatar">
            <UserAvatar name={name} photoURL={photoURL} size={200} />
          </div>
          <div style={{ textAlign: "right" }}>
            {auth.currentUser && (
              <IconHeart
                onClick={handleConnectedClick}
                size={40}
                stroke={isConnected ? 0 : 1.5}
                //fill={isConnected ? "red" : "none"}
                fill={isConnected ? "red" : "none"}
                color="black"
                style={{ cursor: "pointer" }}
                title={intl.formatMessage({ id: "profile.connect" })}
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "50px",
            }}
          >
            <Select
              id="country-select"
              mt="sm"
              label={intl.formatMessage({ id: "profile.country" })}
              data={countries.map((area) => ({
                value: area.code,
                label: area.name,
              }))}
              checkIconPosition="right"
              value={country}
              readOnly
              style={{ flex: 1 }}
            />
            <TextInput
              id="city"
              mt="sm"
              label={intl.formatMessage({ id: "profile.city" })}
              inputWrapperOrder={["label", "input"]}
              value={city}
              readOnly
              style={{ flex: 1 }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "50px",
            }}
          >
            <Select
              id="reason"
              mt="sm"
              label={intl.formatMessage({ id: "profile.reason" })}
              data={lists.reasonOptions.map((option) => ({
                value: option.value,
                label: intl.formatMessage({
                  id: `lookup.reason.${option.value}`,
                }),
              }))}
              checkIconPosition="right"
              value={reason}
              readOnly
              style={{ flex: 1 }}
            />
            <Select
              id="prof-area"
              mt="sm"
              label={intl.formatMessage({ id: "profile.profArea" })}
              data={lists.professionalAreasOptions.map((option) => ({
                value: option.value,
                label: intl.formatMessage({
                  id: `lookup.profArea.${option.value}`,
                }),
              }))}
              checkIconPosition="right"
              value={profArea}
              readOnly
              style={{ flex: 1 }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "50px",
            }}
          >
            <Select
              id="availability"
              mt="sm"
              label={intl.formatMessage({ id: "profile.availability" })}
              data={[
                { value: "0", label: "0" },
                { value: "1-5", label: "1-5" },
                { value: "5-10", label: "5-10" },
                { value: "10-20", label: "10-20" },
                { value: "20+", label: "20+" },
              ]}
              checkIconPosition="right"
              value={availability}
              readOnly
              style={{ flex: 1 }}
            />
            <NumberInput
              id="salary"
              mt="sm"
              label={intl.formatMessage({ id: "profile.salary" })}
              value={salary}
              placeholder={intl.formatMessage({
                id: "profile.salary",
              })}
              style={{ flex: 1 }}
              readOnly
            />
          </div>
          <Textarea
            id="description"
            mt="sm"
            label={intl.formatMessage({ id: "profile.description" })}
            placeholder={intl.formatMessage({ id: "profile.description" })}
            value={description}
            readOnly
            autosize
          />
          <Group justify="end" style={{ marginTop: "16px" }}>
            {auth.currentUser && auth.currentUser.uid !== id && (
              <Button
                disabled={!auth.currentUser}
                className="profile-chat-button"
                //variant="outline"
                onClick={onSendMessageClick}
              >
                <IconSend size={18} style={{ marginRight: 8 }} />
                {intl.formatMessage({ id: "button.chat" })}
              </Button>
            )}
            <Button variant="default" onClick={onCloseButtonClick}>
              {intl.formatMessage({ id: "button.close" })}
            </Button>
          </Group>
          <Group justify="end" style={{ marginTop: "16px" }}>
            {auth.currentUser &&
              auth.currentUser.uid === process.env.REACT_APP_SR_USER_ID && (
                <>
                  <p style={{ color: "red" }}>Admin Options:</p>
                  <TextInput
                    styles={{
                      input: {
                        color: "red",
                      },
                    }}
                    type="email"
                    id="email"
                    value={email}
                    readOnly
                  />
                  <Button
                    style={{ backgroundColor: "red" }}
                    onClick={onDeleteAccountClick}
                  >
                    <IconSend size={18} style={{ marginRight: 8 }} />
                    Delete Account
                  </Button>
                </>
              )}
          </Group>
        </div>
      </div>
    </>
  );
}
